import React, { FormEvent, useState } from "react";
import { Star } from "lucide-react";
import { useParams } from "react-router-dom";
import { baseUrl, create_service_feedback } from "../../API/api";
import customFetch from "../../API/CustomFetch";
import showToastMessage from "../../Toastify/Index";
import Navbar from "../customer-consent/components/Navbar";

const ratingOptions = [1, 2, 3, 4, 5];

const ServiceFeedback: React.FC = () => {
  const { ticketNo } = useParams<{ ticketNo: string }>();
  const [rating, setRating] = useState<number | null>(null);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!ticketNo) {
      showToastMessage(false, "Ticket number is missing in the URL.");
      return;
    }

    if (!rating) {
      showToastMessage(false, "Please select a rating.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: { rating: number; remarks?: string } = { rating };
      const trimmedRemarks = remarks.trim();

      if (trimmedRemarks) {
        payload.remarks = trimmedRemarks;
      }

      const response = await customFetch(
        `${baseUrl}/${create_service_feedback(ticketNo)}`,
        JSON.stringify(payload),
        { method: "POST" },
      );

      if (response?.SUCCESS) {
        setIsSubmitted(true);
        showToastMessage(true, "Feedback submitted successfully.");
      } else {
        showToastMessage(
          false,
          response?.MESSAGE || "Failed to submit feedback.",
        );
      }
    } catch {
      showToastMessage(false, "Failed to submit feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F3F5] px-4 py-8">
      <Navbar />
      <main className="mx-auto mt-6 w-full max-w-xl rounded-2xl bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-[#1A1A1A]">
            Share Your Service Feedback
          </h1>
          <p className="mt-2 text-sm text-[#666666]">
            Rate your recent service experience.
          </p>
        </div>

        {isSubmitted ? (
          <div className="rounded-lg bg-[#E8F8EA] px-4 py-3 text-center text-sm font-medium text-[#0C6B16]">
            Thank you for your feedback.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-[#1A1A1A]">
                Rating *
              </legend>
              <div className="flex items-center gap-2">
                {ratingOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-label={`Rate ${option} star${option === 1 ? "" : "s"}`}
                    aria-pressed={rating === option}
                    onClick={() => setRating(option)}
                    className="rounded p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#00AA13]"
                  >
                    <Star
                      className={
                        option <= (rating || 0)
                          ? "h-8 w-8 fill-[#F5B301] text-[#F5B301]"
                          : "h-8 w-8 text-[#D0D5DD]"
                      }
                    />
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label
                htmlFor="remarks"
                className="mb-2 block text-sm font-medium text-[#1A1A1A]"
              >
                Remarks <span className="font-normal text-[#666666]"></span>
              </label>
              <textarea
                id="remarks"
                rows={5}
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                placeholder="Tell us about your experience"
                className="w-full rounded-lg border border-[#D0D5DD] px-4 py-3 text-sm outline-none transition focus:border-[#00AA13] focus:ring-1 focus:ring-[#00AA13]"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#00AA13] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#008F11] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default ServiceFeedback;
